import type { DecisionListItem, KnowledgeSourceLink } from '@fos/shared/contracts/knowledge';
import { Gavel, Link2 } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Mono } from '../../../ui/Mono';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { TOUCH_TEXT } from '../../mobile';
import { decisionDay } from '../knowledge-format';

/** Źródło decyzji (MVP Etap 3: „źródło decyzji można otworzyć”): link, gdy osoba je widzi. */
export function SourceLink({ source, className }: { source: KnowledgeSourceLink | null; className?: string }) {
  if (!source) return null;
  if (!source.visible) {
    return <span className={cn('text-caption text-fg-muted', className)}>{source.label}</span>;
  }
  const content = (
    <>
      <Link2 size={12} strokeWidth={2} aria-hidden className="shrink-0" />
      <span className="min-w-0 truncate">{source.label}</span>
    </>
  );
  const base = `inline-flex max-w-full items-center gap-1 text-caption ${TOUCH_TEXT}`;
  if (!source.to) return <span className={cn(base, 'text-fg-secondary', className)}>{content}</span>;
  return (
    <Link
      to={source.to}
      aria-label={`Źródło: ${source.label}`}
      className={cn(
        base,
        'rounded-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent',
        className,
      )}
    >
      {content}
    </Link>
  );
}

export function DecisionRow({ decision, showProject = false }: { decision: DecisionListItem; showProject?: boolean }) {
  return (
    <li className="flex gap-4 border-b border-line py-3.5">
      <Mono className="w-11 shrink-0 pt-0.5 text-caption text-fg-muted">{decisionDay(decision.decidedOn)}</Mono>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link
          to={`/wiedza/${decision.id}`}
          className={`rounded-xs text-body-s text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
        >
          {decision.title}
        </Link>
        {showProject && decision.project && <span className="text-caption text-fg-muted">{decision.project.name}</span>}
        <SourceLink source={decision.source} />
      </div>
    </li>
  );
}

/**
 * Lista „Decyzje” (Figma 10; profil klienta, projekt, notatka z ustaleniami): data (mono), treść, źródło do otwarcia.
 * Najnowsze najpierw. `sourceItemId` = decyzje, których źródłem jest dana notatka.
 */
export function DecisionsList({
  clientId,
  projectId,
  sourceItemId,
  limit,
  showProject = false,
  emptyTitle = 'Brak zapisanych decyzji',
  emptyDescription,
}: {
  clientId?: string;
  projectId?: string;
  sourceItemId?: string;
  limit?: number;
  showProject?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const access = useAccess();
  const enabled = access.can('knowledge');
  const query = trpc.knowledge.listDecisions.useQuery(
    {
      ...(clientId ? { clientId } : {}),
      ...(projectId ? { projectId } : {}),
      ...(sourceItemId ? { sourceItemId } : {}),
      ...(limit ? { limit } : {}),
    },
    { enabled },
  );
  if (!enabled) return null;
  if (query.isPending) return <ListSkeleton rows={3} />;
  if (query.isError) {
    return (
      <ErrorState
        compact
        title="Nie udało się wczytać decyzji"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  if (query.data.length === 0) {
    return <EmptyState compact icon={Gavel} title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <ul aria-label="Decyzje">
      {query.data.map((d) => (
        <DecisionRow key={d.id} decision={d} showProject={showProject} />
      ))}
    </ul>
  );
}
